<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="confirmDeleteLocalizationDialog"
            :data="currentLocalization"
            :action-loading="actionLoading.deleteLocalization"
            @on-confirm="deleteLocalization"
            @on-abort="closeConfirmDeleteLocalizationDialog"
        >
            <template #title>
                Usuwanie lokalizacji
            </template>
            <template #content>
                Czy na pewno chcesz usunąć lokalizację?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-row align="center" dense>
            <v-col cols="auto">
                <page-title>Lokalizacje</page-title>
            </v-col>
            <v-col cols="auto">
                <SelectMenu />
            </v-col>
        </v-row>

        <v-row align="center">
            <v-col cols="12" lg="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj lokalizację"
                    @click="createLocalization"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj lokalizację
                </v-btn>
            </v-col>

            <v-col cols="12" sm="8">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" md="8" lg="4">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="localizations"
                        :loading="localizationsLoading"
                        :page.sync="pagination.page"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :items-per-page.sync="pagination.itemsPerPage"
                        :mobile-breakpoint="0"
                        :item-class="setSelectedRow"
                        name="localizationsData"
                        @pagination="updatePagination"
                    >
                        <template #item.number="{item}">
                            {{ item.number }}
                        </template>
                        <template #item.customer="{item}">
                            {{ item.customer.name }}
                        </template>
                        <template #item.city="{item}">
                            {{ item.city }}
                        </template>
                        <template #item.street="{item}">
                            {{ item.street }}
                        </template>
                        <template #item.coordinates="{item}">
                            <v-icon
                                v-if="item.latitude && item.longitude"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Edytuj"
                                            v-on="on"
                                            @click="openLocalization(item.id)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Edytuj</span>
                                </v-tooltip>
                                <v-tooltip top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showConfirmDeleteLocalizationDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import SelectMenu from '@/views/Outdoors/Common/SelectMenu'
import filters from '@/mixins/dataTableFilters'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'OutdoorsList',
    components: {
        PageTitle,
        ConfirmDialog,
        SelectMenu
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('outdoors/outdoorsSettings/clearLocalizationFilters')
            this.clearSelectedListItemId()
        }

        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            currentLocalization: {},
            confirmDeleteLocalizationDialog: false,
            actionLoading: {
                deleteLocalization: false
            },
            actionName: null,
            action: null,
            headers: [
                {
                    text: 'Numer',
                    value: 'number',
                    sortable: false,
                    align: 'center',
                    width: 110
                },
                {
                    text: 'Klient',
                    value: 'customer',
                    sortable: false
                },
                {
                    text: 'Miasto',
                    value: 'city',
                    sortable: false,
                },
                {
                    text: 'Adres',
                    value: 'street',
                    sortable: false,
                },
                {
                    text: 'Współrzędne',
                    value: 'coordinates',
                    sortable: false,
                    align: 'center',
                    width: 80
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        localizations() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizations.localizations
        },
        localizationsLoading() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizations.localizationsLoading
        },
        savedFilters() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizations.filters
        },
        selectedLocalizationId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getLocalizations()
    },
    methods: {
        setSelectedRow(item) {
            if (item.id === this.selectedLocalizationId) {
                return 'isSelected'
            }
        },
        getLocalizations() {
            this.$store.dispatch('outdoors/outdoorsSettings/getLocalizations', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createLocalization() {
            this.$router.push({ name: 'outdoorsSettingsOutdoorsLocalizationsOutdoorsLocalizationCreate' })
                .catch(error => {
                    errorHandler(error)
                })
        },
        openLocalization(localizationId) {
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(localizationId)
            this.$router.push({
                name: 'outdoorsSettingsOutdoorsLocalizationsOutdoorsLocalizationEdit',
                params: { id: localizationId }
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showConfirmDeleteLocalizationDialog(localization) {
            this.currentLocalization = localization
            this.confirmDeleteLocalizationDialog = true
        },
        deleteLocalization() {
            this.actionLoading.deleteLocalization = true

            this.$store.dispatch('outdoors/outdoorsSettings/deleteLocalization', this.currentLocalization.id)
                .then(() => {
                    this.getLocalizations()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeConfirmDeleteLocalizationDialog()
                })
        },
        closeConfirmDeleteLocalizationDialog() {
            this.actionLoading.deleteLocalization = false
            this.confirmDeleteLocalizationDialog = false

            setTimeout(() => {
                this.currentLocalization = {}
            }, 500)
        },
        changeFilter() {
            this.$store.dispatch('outdoors/outdoorsSettings/setLocalizationFilters', this.filters)
            this.resetPagination()
            this.getLocalizations()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.paginatio) {
                this.getLocalizations()
            }
            this.clearPaginationPending()
        }
    }
}
</script>

